import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import api from '../utils/api';

const NotificationContext = createContext();

export function useNotifications() {
  return useContext(NotificationContext);
}

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const [permission, setPermission] = useState('default');
  const [newOrderCount, setNewOrderCount] = useState(0);
  const [latestOrders, setLatestOrders] = useState([]);
  const [pendingShopCount, setPendingShopCount] = useState(0);
  const [pendingShops, setPendingShops] = useState([]);
  const knownPendingIdsRef = useRef(null);
  const lastCheckedRef = useRef(null);
  const audioRef = useRef(null);
  const intervalRef = useRef(null);

  // Initialize audio for notification sound
  useEffect(() => {
    // Create audio element with a simple notification sound
    audioRef.current = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleVkgKW2Vy9yjYBwEOZnb2qlkIBA6ndzarGohDzqa2dqrZiEOOZna2q1pIw44l9nbrWolDjWV2NsvaJAjJDyO1d2rZSMOMpPW3K1nJQ8wktXdr2omDy+R1N2vaiYPLpDU3a5qJg8ukNTer2snDy2Q1N6wbCgPLY/T3rBsKA8tj9Pes20pDyyO0t+zbSkPLI7S37RuKg8rjdLfs24qDyuN0t+0markup7CoOK43S37NtKQ8rjdLftG4qDyuN0t+0bioOK43S37RuKg8rjdLftG4qDyuN0t+0bioOK43S37RuKg8rjdLftG4qDyuN0d+0bysOK4zR37VvLA4rjNHftG8rDiuM0d+1by0OK4zQ37VwLQ4rjNDftXAtDSuMz9+1cC0NK4zP37ZwLQ0qi8/ftnEuDSqLz9+2cS4NKovP37ZxLg0qi8/ftnEuDCqLzt+2cS4MKovO37ZxLgwqi87ftnEuDCqLzt+2cS4MKYvO37dxLgwpi87ft3IuDCmKzd+3ci4MKYrN37dyLgwpis3ft3IuDCmKzd+3ci4MKYrN37dyLgwpis3ft3IuDCmKzd+3ci4MKYrM37hyLwspiszfuHIvCymKzN+4ci8LKYrM37hyLwspiszfuHIvCymKzN+4cS8LKYrM37hxLwspiszfuHEvCyiKzN+5ci8LKIrM37lyLwsoiszfuXIvCyiKy9+5cy8KKIrL37lzLwooicvfunMvCiiJy9+6cy8KKInL37pzLwoniczfunQwCiiJy9+6dDAKJ4nL37p0MAonicrfu3QwCSaJyt+7dTEJJonK37t1MQkmh8rfvHUxCSaHyt+8djEJJofK37x2MQklhsrfvHcyCSWGyt+8dzIJJYbK37x3Mgklhsrfu3cxCSWGyt+7dzEJJYbK37t3MQklhcrfu3gxCSWFyt+7eDEJJYXK37t4MQklhcrfu3gxCSWFyt+7eDEJJYXK37t4MQklhcrfu3gxCSWFyt+7eDEIJYXK37t4MQglhMrfvHkxCCWEyt+8eTEIJYTK37x5MQglhMrfvHkxCCWEyt+8eTEIJYTK37x5MQglhMrfvHkxCCWEyt+8eTEIJYPJ37x5MQgkg8nfvXoxCCSDyd+9ejEIJIPJ3715MQgkg8nfvXoxCCSDyd+9ejEIJIPJ3715MQgkg8nfvXoxCCSDyd+9ejEIJIPJ3715MQgkg8nfvXoxCCSCyN+9ejEIJILI3716MggkgsjfvXoyCCSCyN+9ejIIJILI3716MggkgsjfvXoyCCSCyN+9ejIIJILI3716MggkgsjfvXoyCCSCyN+9ejII');
    return () => {
      if (audioRef.current) {
        audioRef.current = null;
      }
    };
  }, []);

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      console.log('Browser does not support notifications');
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }, []);

  // Show browser notification
  const showNotification = useCallback((title, body, onClick) => {
    if (permission !== 'granted') return;

    try {
      const notification = new Notification(title, {
        body,
        icon: '/logo192.png',
        badge: '/logo192.png',
        tag: 'ed-steel-order',
        requireInteraction: true
      });

      // Play sound
      if (audioRef.current) {
        audioRef.current.play().catch(e => console.log('Audio play failed:', e));
      }

      notification.onclick = () => {
        window.focus();
        notification.close();
        if (onClick) onClick();
      };

      // Auto close after 10 seconds
      setTimeout(() => notification.close(), 10000);
    } catch (error) {
      console.error('Error showing notification:', error);
    }
  }, [permission]);

  // Check for new orders
  const checkForNewOrders = useCallback(async () => {
    if (!user || user.role !== 'admin') return;

    try {
      const response = await api.get('/admin/orders');
      const orders = response.data || [];
      
      if (orders.length === 0) return;

      // Sort by created_at descending
      const sortedOrders = [...orders].sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );

      const latestOrderTime = new Date(sortedOrders[0].created_at).getTime();

      // If this is the first check, just set the reference
      if (lastCheckedRef.current === null) {
        lastCheckedRef.current = latestOrderTime;
        return;
      }

      // Find new orders since last check
      const newOrders = sortedOrders.filter(order => 
        new Date(order.created_at).getTime() > lastCheckedRef.current
      );

      if (newOrders.length > 0) {
        setNewOrderCount(prev => prev + newOrders.length);
        setLatestOrders(newOrders);

        // Show notification for each new order
        newOrders.forEach(order => {
          const orderNumber = order.id.slice(0, 8).toUpperCase();
          const total = (order.total || 0).toFixed(2);
          showNotification(
            '🔔 Neue Bestellung bei ED STEEL!',
            `Bestellung #${orderNumber} - €${total}`,
            () => {
              window.location.href = '/admin';
            }
          );
        });

        // Update last checked time
        lastCheckedRef.current = latestOrderTime;
      }
    } catch (error) {
      console.error('Error checking for new orders:', error);
    }
  }, [user, showNotification]);

  // Check for reseller shops awaiting approval
  const checkForPendingShops = useCallback(async () => {
    if (!user || user.role !== 'admin') return;

    try {
      const response = await api.get('/admin/reseller-shops');
      const shops = response.data || [];
      const pending = shops.filter(s => !s.is_active);

      setPendingShopCount(pending.length);
      setPendingShops(pending);

      const pendingIds = new Set(pending.map(s => s.id));

      // First check: remember current state without notifying
      if (knownPendingIdsRef.current === null) {
        knownPendingIdsRef.current = pendingIds;
        return;
      }

      // Notify only for newly appeared pending shops
      const newlyPending = pending.filter(s => !knownPendingIdsRef.current.has(s.id));
      newlyPending.forEach(shop => {
        showNotification(
          '🆕 Neuer Händler wartet auf Freigabe!',
          `${shop.shop_name} (${shop.owner?.company_name || shop.contact_email || ''}) muss aktiviert werden.`,
          () => {
            window.location.href = '/admin?tab=reseller-shops';
          }
        );
      });

      knownPendingIdsRef.current = pendingIds;
    } catch (error) {
      console.error('Error checking for pending shops:', error);
    }
  }, [user, showNotification]);

  // Clear new order count
  const clearNewOrderCount = useCallback(() => {
    setNewOrderCount(0);
    setLatestOrders([]);
  }, []);

  // Start/stop polling based on admin status
  useEffect(() => {
    if (user && user.role === 'admin') {
      // Start polling every 30 seconds
      checkForNewOrders(); // Initial check
      checkForPendingShops();
      intervalRef.current = setInterval(() => {
        checkForNewOrders();
        checkForPendingShops();
      }, 30000);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    } else {
      // Clear interval if not admin
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      lastCheckedRef.current = null;
      knownPendingIdsRef.current = null;
    }
  }, [user, checkForNewOrders, checkForPendingShops]);

  // Request permission when admin logs in (separate effect to avoid lint warning)
  useEffect(() => {
    if (user && user.role === 'admin' && permission === 'default') {
      // Use setTimeout to avoid synchronous state update in effect
      const timer = setTimeout(() => {
        requestPermission();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [user, permission, requestPermission]);

  const value = {
    permission,
    requestPermission,
    newOrderCount,
    latestOrders,
    clearNewOrderCount,
    showNotification,
    pendingShopCount,
    pendingShops,
    refreshPendingShops: checkForPendingShops
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}
